import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { httpGet, httpPost, httpPut } from '../../helpers/http.js';
import {
    createPublishAsCompositionThunk,
    createUnpublishAsCompositionThunk,
    createLoadElearningItemsThunk,
} from '../elearning/elearning.js';
import { assignAndIsDirty } from '../../helpers/extend.js';

// An LTI 1.3 item lives in two places, like an LTI 1.1 item: the ElearningItem in CompositionApi
// (title, description, launch type, catalog state) and the resource link in the LTI Advantage
// Platform (deployment, launch url, custom parameters, mastery score, attempts). The editor
// orchestrates both; `current` is the ElearningItem with its `resourceLink` attached.

export const LTI_ADVANTAGE_SUPPLIER = { supplierKey: 'LTIAdvantage', name: 'LTI Advantage' };

const resourceLinksUrl = (env) => `${env.settings.ltiAdvantageApi}/api/portals/${env.portalIdentifier}/resourcelinks`;

const packageUrl = (env) => `${env.settings.compositionApi}/package/lti-advantage`;

export const getLastVersion = (item) => item?.versions?.length
    ? item.versions[item.versions.length - 1]
    : null;

// The platform stores the resource link id in startData; items published by the platform use the
// same id for the ElearningItem, so the targetId is the fallback.
const getResourceLinkId = (item) => getLastVersion(item)?.startData?.resourceLink || item.targetId;

// The platform only serves resource links of the portal's own deployments. GLOBAL has none and a
// link of another portal is a 404, so a failing lookup yields null instead of failing the whole load.
const loadResourceLink = async (env, auth, item) => {
    if (env.hasGlobalId) {
        return null;
    }

    try {
        return await httpGet(`${resourceLinksUrl(env)}/${getResourceLinkId(item)}`, auth.user);
    } catch (error) {
        console.warn(`Resource link for ${item.targetId} is not available for portal ${env.portalIdentifier}`, error);
        return null;
    }
};

const loadItemWithResourceLink = async (env, auth, targetId) => {
    const item = await httpGet(`${env.settings.compositionApi}/elearningitems/${targetId}`, auth.user);
    const resourceLink = await loadResourceLink(env, auth, item);

    return { ...item, resourceLink };
};

const toResourceLinkRequest = (item) => ({
    contextId: item.resourceLink.contextId,
    title: item.title,
    description: item.description,
    launchUrl: item.resourceLink.launchUrl,
    customParams: item.resourceLink.customParams,
    masteryScore: item.resourceLink.masteryScore,
    maxAttempts: item.resourceLink.maxAttempts,
});

export const loadLtiAdvantageItemsIfNeeded = createLoadElearningItemsThunk('ltiAdvantage', 'ltiAdvantage');

export const publishLtiAdvantageItemAsComposition = createPublishAsCompositionThunk('ltiAdvantage');

export const unpublishLtiAdvantageItemAsComposition = createUnpublishAsCompositionThunk('ltiAdvantage');

export const loadLtiAdvantageItem = createAsyncThunk(
    'ltiAdvantage/fetchOne',
    async (args, { getState }) => {
        const { targetId } = args;
        const { env, auth } = getState();

        return loadItemWithResourceLink(env, auth, targetId);
    },
);

export const saveLtiAdvantageItem = createAsyncThunk(
    'ltiAdvantage/save',
    async (_, { getState }) => {
        const { ltiAdvantage: state, env, auth } = getState();
        const { current: item } = state;

        if (item.resourceLink) {
            await httpPut(`${resourceLinksUrl(env)}/${item.resourceLink.uniqueId}`, auth.user, toResourceLinkRequest(item));
        }

        // Metadata-only save: re-send the last version's start data so the version is replaced, not added.
        const lastVersion = getLastVersion(item);
        await httpPost(packageUrl(env), auth.user, {
            portalId: env.portalIdentifier,
            supplierKey: item.supplier?.supplierKey,
            supplierName: item.supplier?.name,
            publisherIdString: auth.user.profile.sub,
            publisherName: auth.user.profile.name,
            items: [
                {
                    targetId: item.targetId,
                    title: item.title,
                    description: item.description,
                    launchType: item.launchType,
                    startUrl: lastVersion?.startUrl,
                    startData: lastVersion?.startData,
                    autoComplete: lastVersion?.autoComplete,
                },
            ],
            updateMode: 0, // 'update'
        });

        return loadItemWithResourceLink(env, auth, item.targetId);
    },
);

export const createLtiAdvantageItem = createAsyncThunk(
    'ltiAdvantage/create',
    async (args, { getState }) => {
        const { name, contextId } = args;
        const { env, auth } = getState();

        const resourceLink = await httpPost(resourceLinksUrl(env), auth.user, {
            contextId: contextId,
            title: name,
        });

        await httpPost(packageUrl(env), auth.user, {
            portalId: env.portalIdentifier,
            supplierKey: LTI_ADVANTAGE_SUPPLIER.supplierKey,
            supplierName: LTI_ADVANTAGE_SUPPLIER.name,
            publisherIdString: auth.user.profile.sub,
            publisherName: auth.user.profile.name,
            items: [
                {
                    targetId: resourceLink.uniqueId,
                    title: name,
                    type: 'microlearning',
                    startData: {
                        resourceLink: resourceLink.uniqueId,
                        launchTokenUrl: env.settings.ltiAdvantageApi,
                    },
                },
            ],
            updateMode: 1, // 'newVersion'
        });

        return {
            targetId: resourceLink.uniqueId,
            title: name,
            description: null,
            playerType: 3, // 'lti-advantage'
            elearningType: 1, // microlearning
            lastModified: new Date(),
            supplier: LTI_ADVANTAGE_SUPPLIER,
        };
    },
);

const ltiAdvantageSlice = createSlice({
    name: 'ltiAdvantage',
    initialState: {
        status: 'idle',
        list: [],
        error: null,
        page: -1,
        current: null,
        dirty: false,
    },
    reducers: {
        updateLtiAdvantageItem: (state, action) => {
            const { payload } = action;

            if (assignAndIsDirty(state.current, payload.changes)) {
                state.dirty = true;
            }
        },
        updateLtiAdvantageItemParameters: (state, action) => {
            const { payload } = action;

            state.current.resourceLink.customParams = { ...payload };
            state.dirty = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadLtiAdvantageItemsIfNeeded.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadLtiAdvantageItemsIfNeeded.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.list = payload.reload ? [...payload.list] : [...state.list, ...payload.list];
                state.page = payload.page;
                state.total = payload.total;
                state.search = payload.search;
            })
            .addCase(loadLtiAdvantageItemsIfNeeded.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(loadLtiAdvantageItem.pending, (state) => {
                state.status = 'loading';
                state.current = null;
                state.dirty = false;
            })
            .addCase(loadLtiAdvantageItem.fulfilled, (state, action) => {
                state.status = 'success';
                state.current = action.payload;
                state.dirty = false;
            })
            .addCase(loadLtiAdvantageItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(saveLtiAdvantageItem.pending, (state) => {
                state.status = 'saving';
            })
            .addCase(saveLtiAdvantageItem.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.current = payload;
                state.dirty = false;

                const listItem = state.list.find((i) => i.targetId === payload.targetId);
                if (listItem) {
                    Object.assign(listItem, {
                        title: payload.title,
                        description: payload.description,
                        lastModified: new Date(),
                    });
                }
            })
            .addCase(saveLtiAdvantageItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(createLtiAdvantageItem.pending, (state) => {
                state.status = 'creating';
            })
            .addCase(createLtiAdvantageItem.fulfilled, (state, action) => {
                state.status = 'success';
                state.list.unshift(action.payload);
                state.total = (state.total || 0) + 1;
            })
            .addCase(createLtiAdvantageItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(publishLtiAdvantageItemAsComposition.pending, (state) => {
                state.status = 'publishing';
            })
            .addCase(publishLtiAdvantageItemAsComposition.fulfilled, (state, action) => {
                const { payload } = action;
                let ltiItem = state.list.find((i) => i.targetId === payload.targetId);

                state.status = 'success';
                ltiItem = Object.assign(ltiItem, payload);
            })
            .addCase(publishLtiAdvantageItemAsComposition.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(unpublishLtiAdvantageItemAsComposition.pending, (state) => {
                state.status = 'unpublishing';
            })
            .addCase(unpublishLtiAdvantageItemAsComposition.fulfilled, (state, action) => {
                const { payload } = action;
                let ltiItem = state.list.find((i) => i.targetId === payload.targetId);

                state.status = 'success';
                ltiItem = Object.assign(ltiItem, payload);
            })
            .addCase(unpublishLtiAdvantageItemAsComposition.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            });
    },
});

export const { updateLtiAdvantageItem, updateLtiAdvantageItemParameters } = ltiAdvantageSlice.actions;

export default ltiAdvantageSlice.reducer;
